import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { DEMOGRAPHICS_SECTION_IDS } from "react-migration/constants";
import { userStore } from "src/js/stores/user/store";
import Feature from "src/js/stores/user/Feature";
import * as Tabs from "../map/Multilayer/Bundle/Workbench/AssessmentTab/layersByTab";
import { AssessmentLayerTabUK } from "../map/Multilayer/Bundle/Workbench/AssessmentTab/types";
import {
  UK_ENVIRONMENTAL_SITE_REPORT_LAYERS,
  UK_POLICY_SITE_REPORT_LAYERS,
} from "../map/Multilayer/layers/significantConstraintsLayer";
import { useInsightSummaryLayers } from "../map/Multilayer/Bundle/Workbench/AssessmentTab/InsightsSummaryCard/useInsightSummaryLayers";
import { useLayerAccess } from "../map/Multilayer/Bundle/useLayerAccess";
import { AssessmentTab, LayerGroup } from "./types";
import { InsightSummaryPrintable } from "../map/Multilayer/Bundle/Workbench/AssessmentTab/InsightsSummaryCard/InsightSummaryPrintable";
import { BundleId } from "src/js/stores/map/store";
import { Layer } from "../map/Multilayer/types";

const EXPANDABLE_TABS = ["details", "compsAndLocal", "planningHistory"];

const SITE_REPORT_LAYERS_BY_TAB = {
  US: Tabs.US_ASSESSMENT_LAYER_BY_TAB,
  UK: {
    ...Tabs.UK_ASSESSMENT_LAYER_BY_TAB,
    [AssessmentLayerTabUK.POLICIES]: [
      // The primary significant constraint layer is hidden from report via
      // `excludeFromReport` config & broken into small layers.
      ...UK_POLICY_SITE_REPORT_LAYERS,
      ...Tabs.UK_ASSESSMENT_LAYER_BY_TAB[AssessmentLayerTabUK.POLICIES],
    ],
    [AssessmentLayerTabUK.ENVIRONMENTAL]: [
      ...UK_ENVIRONMENTAL_SITE_REPORT_LAYERS,
      ...Tabs.UK_ASSESSMENT_LAYER_BY_TAB[AssessmentLayerTabUK.ENVIRONMENTAL],
    ],
  },
};

export function useAssessmentTabs(): AssessmentTab[] {
  const { features } = useSnapshot(userStore.user);
  const isUSUser = features.includes(Feature.usAccess);
  const [insights] = useInsightSummaryLayers(isUSUser);
  const [accessibleInsights] = useLayerAccess(insights);

  // Get all layers across all tabs (static, doesn't depend on user features)
  const allLayers = [
    ...(isUSUser ? Object.values(SITE_REPORT_LAYERS_BY_TAB.US) : Object.values(SITE_REPORT_LAYERS_BY_TAB.UK)),
  ].flat();
  const [accessibleLayers] = useLayerAccess(allLayers);
  const accessibleLayersById = useMemo(
    () => new Map(accessibleLayers.map((layer) => [layer.id, layer])),
    [accessibleLayers]
  );

  return useMemo(() => {
    const tabsConfig = SITE_REPORT_LAYERS_BY_TAB[features.includes(Feature.usAccess) ? "US" : "UK"];

    const result = Object.entries(tabsConfig)
      .map(([key, layers]) => {
        // Filter layers that are both accessible AND printable for this tab
        const accessibleTabLayers = layers.filter((layer) => accessibleLayersById.has(layer.id));
        const filteredLayers = filterPrintableLayers(accessibleTabLayers);
        const expandable = EXPANDABLE_TABS.includes(key);

        let groups: LayerGroup[];

        switch (key) {
          case "details":
            groups = processDetailsTab(filteredLayers, accessibleInsights);
            break;
          case "compsAndLocal":
            groups = processCompsAndLocalTab(filteredLayers);
            break;
          case "planningHistory":
            groups = processPlanningHistoryTab(filteredLayers, key);
            break;
          default:
            groups = processDefaultTab(filteredLayers, key);
        }

        return {
          key,
          expandable,
          groups,
        };
      })
      .filter(({ groups }) => groups.length > 0);

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, accessibleInsights, accessibleLayersById]);
}

function filterPrintableLayers(layers: Layer[]): Layer[] {
  return layers.filter((layer) => {
    const hasPrintable = !!layer.layerType?.Printable;
    const notExcluded = !layer.excludeFromReport;

    return hasPrintable && notExcluded;
  });
}

function filterLayersByIdPattern(layers: Layer[], pattern: string): Layer[] {
  return layers.filter((layer) => layer.id.toLowerCase().includes(pattern.toLowerCase()));
}

function createLayerGroup(
  id: string,
  titleKey: string,
  layers: Layer[],
  labels?: Record<string, string>,
  sectionIds?: string[]
): LayerGroup {
  return {
    id,
    titleKey,
    layerIds: layers.map((l) => l.id),
    layers,
    ...(labels && { labels }),
    ...(sectionIds && { sectionIds }),
  };
}

function createLabelsMap(layers: Layer[]): Record<string, string> {
  return layers.reduce((acc, layer) => {
    acc[layer.id] = layer.title ?? layer.id;
    return acc;
  }, {} as Record<string, string>);
}

function processDetailsTab(filteredLayers: Layer[], accessibleInsights: Layer[]): LayerGroup[] {
  const groups: LayerGroup[] = [];

  const ownership = filterLayersByIdPattern(filteredLayers, "ownership");
  if (ownership.length > 0) {
    groups.push(createLayerGroup("ownership", "site_report.tabs.details.ownership", ownership));
  }

  const propertyInformation = filterLayersByIdPattern(filteredLayers, "property_information");
  if (propertyInformation.length > 0) {
    groups.push(
      createLayerGroup("propertyInformationGroup", "site_report.tabs.details.propertyInformation", propertyInformation)
    );
  }

  const topography = filterLayersByIdPattern(filteredLayers, "topographylayer");
  if (topography.length > 0) {
    groups.push(createLayerGroup("topography", "site_report.tabs.details.topography", topography));
  }

  const printableInsights = accessibleInsights
    .filter((layer) => !layer.excludeFromReport && layer.id !== "PROPERTY_INFORMATION")
    .map((layer) => ({
      ...layer,
      layerType: {
        ...layer.layerType,
        Printable: (layer.id !== "amenities" && layer.layerType.Printable) || InsightSummaryPrintable,
      },
    }));

  if (printableInsights.length > 0) {
    const insightIds = printableInsights.map((l) => l.id);
    groups.push(
      createLayerGroup(
        "insights",
        "site_report.tabs.insights",
        printableInsights,
        createLabelsMap(printableInsights),
        insightIds
      )
    );
  }

  return groups;
}

function processCompsAndLocalTab(filteredLayers: Layer[]): LayerGroup[] {
  const groups: LayerGroup[] = [];

  const comparablesLayers = filterLayersByIdPattern(filteredLayers, "comparable");
  if (comparablesLayers.length > 0) {
    groups.push(
      createLayerGroup(
        "comparables",
        "site_report.layers.comparables",
        comparablesLayers,
        createLabelsMap(comparablesLayers)
      )
    );
  }

  const demographicsLayers = filteredLayers.filter(
    (layer) =>
      (layer.id === "uk_demographics" || layer.id === "us_demographics") &&
      layer.layerType?.printableConfig?.supportsSectionToggle
  );

  if (demographicsLayers.length > 0) {
    // Note: sectionIds determination would need user features here if we want to keep US/UK distinction
    // For now, using a fallback - this might need adjustment based on how demographics should be handled
    const sectionIds = DEMOGRAPHICS_SECTION_IDS; // Default to UK, or add features param back if needed
    const demographicsSectionLabels = sectionIds.reduce((acc: Record<string, string>, sectionId: string) => {
      acc[sectionId] = `site_report.layers.demographics.${sectionId}`;
      return acc;
    }, {} as Record<string, string>);

    groups.push(
      createLayerGroup(
        "demographics",
        "site_report.layers.demographics",
        demographicsLayers,
        demographicsSectionLabels,
        sectionIds
      )
    );
  }

  const amenitiesLayers = filteredLayers.filter((layer) => layer.id.includes("amenities"));
  if (amenitiesLayers.length > 0) {
    groups.push(createLayerGroup("amenities", "site_report.layers.amenities", amenitiesLayers));
  }

  return groups;
}

function isSummaryLayer(layer: Layer): boolean {
  return layer.layerType.LayerSummary != null;
}

function processPlanningHistoryTab(filteredLayers: Layer[], key: string): LayerGroup[] {
  if (filteredLayers.length === 0 || filteredLayers.every(isSummaryLayer)) {
    return [];
  }

  return [createLayerGroup(key, `site_report.tabs.${key}`, filteredLayers)];
}

function processDefaultTab(filteredLayers: Layer[], key: string): LayerGroup[] {
  if (filteredLayers.length === 0) {
    return [];
  }

  return [createLayerGroup(key, `site_report.tabs.${key}`, filteredLayers)];
}
